import { Formik } from "formik";
import * as yup from "yup";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { Button } from "@repo/ui/components/Base/Button";
import type { UserImportData } from "../UsersPreviewTable";
import {
  type UserImportBindingConfig,
  userImportService,
} from "@/services/userImportService";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import type { UserDuplicate } from "@repo/types";

const validationSchema = yup.object({
  email: yup.string().required("Email is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
});

const autoMappingConfig = {
  firstName: ["firstName", "first name"],
  lastName: ["lastName", "last name"],
  email: ["email"],
  salutation: ["salutation"],
  role: ["role", "type"],
  primaryAddress: {
    type: ["addressType", "address type"],
    street: ["street"],
    street2: ["street2", "Apt / Suite", "Apt", "Suite"],
    city: ["city"],
    state: ["state"],
    zip: ["zip"],
    country: ["country"],
  },
};

export interface UserImportBindColumnsStepFormProps {
  bindingConfig: UserImportBindingConfig | null;
  headers: string[];
  fileId: string;
  tenantId: string;
  onCancel: () => void;
  onNext: () => void;
  setConfig: (bindingConfig: UserImportBindingConfig) => void;
  setDuplicates: (duplicates: UserDuplicate[]) => void;
  setPreview: (users: UserImportData[]) => void;
  setTotalRecords: (count: number) => void;
}

const findColumnName = (headers: string[], mappingNames: string[]) => {
  const normalized = new Set(mappingNames.map((item) => item.toLowerCase()));

  for (const item of headers) {
    if (normalized.has(item.toLowerCase())) {
      return item;
    }
  }
  return "";
};

const getAutoConfig = (headers: string[]): UserImportBindingConfig => {
  return {
    firstName: findColumnName(headers, autoMappingConfig.firstName),
    lastName: findColumnName(headers, autoMappingConfig.lastName),
    email: findColumnName(headers, autoMappingConfig.email),
    role: findColumnName(headers, autoMappingConfig.role),
    salutation: findColumnName(headers, autoMappingConfig.salutation),
    primaryAddress: {
      type: findColumnName(headers, autoMappingConfig.primaryAddress.type),
      street: findColumnName(headers, autoMappingConfig.primaryAddress.street),
      street2: findColumnName(
        headers,
        autoMappingConfig.primaryAddress.street2
      ),
      city: findColumnName(headers, autoMappingConfig.primaryAddress.city),
      state: findColumnName(headers, autoMappingConfig.primaryAddress.state),
      zip: findColumnName(headers, autoMappingConfig.primaryAddress.zip),
      country: findColumnName(
        headers,
        autoMappingConfig.primaryAddress.country
      ),
    },
  };
};

export const UserImportBindColumnsStepForm = ({
  bindingConfig,
  fileId,
  tenantId,
  headers,
  onCancel,
  onNext,
  setConfig,
  setDuplicates,
  setPreview,
  setTotalRecords,
}: UserImportBindColumnsStepFormProps) => {
  const options: { key: string; value: string }[] = headers.map((header) => {
    return { key: header, value: header };
  });
  options.unshift({ key: "(Not selected)", value: "(Not selected)" });
  return (
    <Formik
      initialValues={
        bindingConfig
          ? ({
              ...bindingConfig,
            } as UserImportBindingConfig)
          : getAutoConfig(headers)
      }
      onSubmit={async (values) => {
        setConfig(values);
        const duplicates = await userImportService.getUserDuplicates({
          fileId,
          tenantId,
          bindConfig: values,
        });
        const previewUsersData = await userImportService.getImportPreview({
          fileId,
          tenantId,
          bindConfig: values,
        });
        setDuplicates(duplicates);
        setPreview(previewUsersData.previews ?? []);
        setTotalRecords(previewUsersData.totalRecords);
        onNext();
      }}
      validationSchema={validationSchema}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        errors,
        touched,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <DialogBody className="flex flex-col gap-y-4 pb-0">
              <p className="text-slate-800 text-base leading-5 py-3">
                Match the fields listed with the columns in the file using the
                dropdown box for each field
              </p>

              <div className="flex flex-col">
                <SelectInput
                  label="*Email"
                  value={values.email}
                  placeholder="Select a field with email"
                  onValueChange={(value) => setFieldValue("email", value)}
                  options={headers.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                  error={!!errors.email && touched.email}
                  errorMessage={errors.email}
                />
                <SelectInput
                  label="*First name"
                  value={values.firstName}
                  placeholder="Select a field with first name"
                  onValueChange={(value) => setFieldValue("firstName", value)}
                  options={headers.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                  error={!!errors.firstName && touched.firstName}
                  errorMessage={errors.firstName}
                />
                <SelectInput
                  label="*Last Name"
                  value={values.lastName}
                  placeholder="Select a field with last name"
                  onValueChange={(value) => setFieldValue("lastName", value)}
                  options={headers.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                  error={!!errors.lastName && touched.lastName}
                  errorMessage={errors.lastName}
                />
                <SelectInput
                  label="Role"
                  value={values.role}
                  placeholder="Select a field with role"
                  onValueChange={(value) => setFieldValue("role", value)}
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="Address type"
                  value={values.primaryAddress.type}
                  placeholder="Select a field with address type"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      type: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="Street"
                  value={values.primaryAddress.street}
                  placeholder="Select a field with street"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      street: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="Apt / Suite"
                  value={values.primaryAddress.street2}
                  placeholder="Select a field with Apt / Suite information"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      street2: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="City"
                  value={values.primaryAddress.city}
                  placeholder="Select a field with city"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      city: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="State"
                  value={values.primaryAddress.state}
                  placeholder="Select a field with state"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      state: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="Zip"
                  value={values.primaryAddress.zip}
                  placeholder="Select a field with zip"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      zip: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
                <SelectInput
                  label="County"
                  value={values.primaryAddress.country}
                  placeholder="Select a field with country"
                  onValueChange={(value) =>
                    setFieldValue("primaryAddress", {
                      ...values.primaryAddress,
                      country: value,
                    })
                  }
                  options={options.map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                  orientation="vertical"
                />
              </div>
            </DialogBody>
            <DialogFooter className="flex flex-row justify-between">
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                Back
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Next
              </Button>
            </DialogFooter>
          </form>
        );
      }}
    </Formik>
  );
};
